import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/formatError";
import { Target, Lock, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) { setErr("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8" data-testid="reset-password-page"
      style={{ background: "radial-gradient(circle at 20% 20%, rgba(139,92,246,0.25), transparent 40%), #0a0a1a" }}>
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-8">
          <img src="/logo.png" alt="Curcompass logo" className="w-10 h-10 rounded-xl object-cover" />
          <div className="font-bold text-xl text-white">Curcompass</div>
        </div>

        {done ? (
          <div className="jr-card text-center" data-testid="reset-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <div className="text-white font-semibold">Password updated!</div>
            <div className="text-zinc-400 text-sm mt-1">Redirecting you to sign in...</div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <h1 className="text-2xl font-bold text-white">Set a new password</h1>
            <p className="text-zinc-400 text-sm">Choose a strong password (min 8 characters).</p>
            <div className="relative">
              <Lock className="w-4 h-4 text-zinc-400 absolute left-3 top-3.5" />
              <input data-testid="reset-password-input" type="password" required minLength={8}
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="New password" className="jr-input !pl-10" />
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 text-zinc-400 absolute left-3 top-3.5" />
              <input data-testid="reset-confirm-input" type="password" required minLength={8}
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm new password" className="jr-input !pl-10" />
            </div>
            {err && <div className="text-rose-300 text-sm" data-testid="reset-error">{err}</div>}
            <button type="submit" disabled={loading || !token} className="jr-btn-primary w-full py-3" data-testid="reset-submit-btn">
              {loading ? "Updating..." : "Update Password"}
            </button>
            {!token && <div className="text-amber-300 text-xs">Missing reset token — please use the link from your email.</div>}
          </form>
        )}
      </div>
    </div>
  );
}
